/* Rinashi A/B measurement bridge.
 *
 * Legacy beacons remain active while Shopify-native v4 runs in shadow. Native
 * exposure/ATC use both Shopify's Customer Events bus and the signed app proxy
 * with one shared event id, so the collector can deduplicate the two paths.
 * Purchases are never inferred in the browser: the exact assignment and epoch
 * are stamped on the tested parent line for Admin GraphQL attribution.
 */
(() => {
  'use strict';

  const ab = window.__rinashiAB;
  if (!ab || ab.preview) return;

  const NATIVE_VERSION = 'native-v4';
  const legacyEndpoint = (document.documentElement.dataset.abEndpoint || '').trim();
  let pendingTestedAdd = null;
  let cartEventsBound = false;

  const randomId = () => (crypto.randomUUID && crypto.randomUUID())
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const sessionId = (() => {
    try {
      let value = sessionStorage.getItem('rinashi_ab_sid');
      if (!value) {
        value = randomId();
        sessionStorage.setItem('rinashi_ab_sid', value);
      }
      return value;
    } catch {
      return randomId();
    }
  })();

  const oncePerSession = (scope, metric) => {
    const key = `rinashi_ab:${scope}:${ab.test}:${ab.variant}:${metric}`;
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
    } catch {
      /* server-side event-id deduplication remains available */
    }
    return true;
  };

  const sendLegacy = (metric) => {
    if (!oncePerSession('legacy', metric) || !legacyEndpoint) return;
    try {
      const body = JSON.stringify({
        e: metric,
        t: ab.test,
        v: ab.variant,
        h: ab.handle,
        s: sessionId,
        m: 'legacy',
      });
      if (
        !navigator.sendBeacon
        || !navigator.sendBeacon(legacyEndpoint, new Blob([body], { type: 'text/plain' }))
      ) {
        fetch(legacyEndpoint, {
          method: 'POST',
          body,
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
        }).catch(() => {});
      }
    } catch {
      /* measurement must never interrupt the storefront */
    }
  };

  const nativeData = (eventId, extra = {}) => ({
    test: ab.test,
    variant: ab.variant,
    handle: ab.handle,
    measurementVersion: NATIVE_VERSION,
    sessionId,
    eventId,
    ...extra,
  });

  const publishNative = (name, data, attempt = 0) => {
    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, data);
        return;
      }
      if (attempt < 10) setTimeout(() => publishNative(name, data, attempt + 1), 500);
    } catch {
      /* app-proxy delivery remains available */
    }
  };

  const whenAnalyticsAllowed = (callback) => {
    let completed = false;
    const deliver = () => {
      if (completed) return;
      completed = true;
      callback();
    };
    const check = () => {
      try {
        const privacy = window.Shopify?.customerPrivacy;
        if (!privacy?.analyticsProcessingAllowed) return false;
        if (privacy.analyticsProcessingAllowed()) {
          deliver();
          return true;
        }
        const onConsent = (event) => {
          if (!event.detail?.analyticsAllowed) return;
          document.removeEventListener('visitorConsentCollected', onConsent);
          deliver();
        };
        document.addEventListener('visitorConsentCollected', onConsent);
        return true;
      } catch {
        return false;
      }
    };

    if (check()) return;
    try {
      window.Shopify?.loadFeatures?.([
        { name: 'consent-tracking-api', version: '0.1' },
      ], (error) => {
        if (!error) check();
      });
    } catch {
      /* Web Pixel remains the consent-aware primary path */
    }
  };

  const sendNative = (metric, name, extra = {}) => {
    if (!oncePerSession(NATIVE_VERSION, metric)) return;
    const eventId = `${NATIVE_VERSION}:${ab.test}:${metric}:${randomId()}`;
    const data = nativeData(eventId, extra);
    publishNative(name, data);
    whenAnalyticsAllowed(() => {
      try {
        fetch('/apps/rinashi-ab', {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify({
            e: metric,
            t: ab.test,
            v: ab.variant,
            m: NATIVE_VERSION,
            s: sessionId,
            id: eventId,
            source: 'shopify-app-proxy',
          }),
        }).catch(() => {});
      } catch {
        /* Shopify pixel delivery remains available */
      }
    });
  };

  const updateCartAttributes = (includePurchaseIntent = false) => {
    try {
      const attributes = { _abm: NATIVE_VERSION };
      attributes[`_ab_${ab.test}`] = ab.variant;
      if (includePurchaseIntent) attributes[`_abp_${ab.test}`] = ab.variant;
      return fetch('/cart/update.js', {
        method: 'POST',
        keepalive: includePurchaseIntent,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      }).catch(() => null);
    } catch {
      return Promise.resolve(null);
    }
  };

  const upsertProperty = (form, property, value) => {
    let input = form.querySelector(`input[data-ab-property="${property}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.dataset.abProperty = property;
      form.appendChild(input);
    }
    input.name = `properties[${property}]`;
    input.value = value;
  };

  const tagTestedLineItem = (form) => {
    try {
      upsertProperty(form, `_ab_${ab.test}`, ab.variant);
      upsertProperty(form, '_abm', NATIVE_VERSION);
    } catch {
      /* never block add-to-cart */
    }
  };

  // Shadow native exposure while the legacy dashboard remains authoritative.
  sendLegacy('view');
  sendNative('view', 'rinashi_ab:exposure');
  updateCartAttributes();

  // Capture phase runs before Dawn serializes FormData. Drawer/upsell forms
  // are intentionally excluded.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches?.('form[data-type="add-to-cart-form"]')) return;
    if (!form.closest('.pdp-buy__ptf, .pdp-sticky-atc__ptf')) return;
    tagTestedLineItem(form);
    pendingTestedAdd = {
      productVariantId: String(new FormData(form).get('id') || ''),
    };
    // Preserve the old metric until the epoch switches. Native ATC waits for
    // Dawn's successful cartUpdate below.
    sendLegacy('atc');
  }, true);

  const bindCartEvents = () => {
    if (cartEventsBound) return true;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    cartEventsBound = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!pendingTestedAdd || event?.source !== 'product-form') return;
      if (
        pendingTestedAdd.productVariantId
        && event?.productVariantId
        && String(event.productVariantId) !== pendingTestedAdd.productVariantId
      ) return;
      const productVariantId = pendingTestedAdd.productVariantId;
      pendingTestedAdd = null;
      const stamp = updateCartAttributes(true);
      sendNative('atc', 'rinashi_ab:atc_confirmed', { productVariantId });
      return stamp;
    });

    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      if (event?.source === 'product-form') pendingTestedAdd = null;
    });
    return true;
  };

  if (!bindCartEvents()) {
    document.addEventListener('DOMContentLoaded', bindCartEvents, { once: true });
    window.addEventListener('load', bindCartEvents, { once: true });
    setTimeout(bindCartEvents, 0);
  }

  // Legacy-only IC. Native IC comes from Shopify checkout_started, including
  // accelerated and Shop Pay paths that never touch this click listener.
  document.addEventListener('click', (event) => {
    const checkout = event.target.closest?.(
      'button[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]',
    );
    if (checkout) sendLegacy('ic');
  });
})();
